"use client";

import { type ReactNode, useMemo, useState } from "react";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";

/* ------------------------------------------------------------------ */
/*  Regex + exception list (module level per Biome rule)              */
/* ------------------------------------------------------------------ */

const RE_LY_WORD = /\b[A-Za-z]+ly\b/g;
const RE_SPLIT_WORDS = /\s+/;

/* Common -ly words that are NOT adverbs */
const LY_EXCEPTIONS = new Set([
  "family",
  "only",
  "early",
  "likely",
  "unlikely",
  "supply",
  "apply",
  "reply",
  "multiply",
  "imply",
  "comply",
  "rely",
  "july",
  "holy",
  "ugly",
  "silly",
  "belly",
  "rally",
  "jelly",
  "ally",
  "fly",
  "butterfly",
  "italy",
  "assembly",
  "monopoly",
  "anomaly",
  "melancholy",
  "bully",
  "chilly",
  "curly",
  "deadly",
  "elderly",
  "friendly",
  "jolly",
  "lily",
  "lonely",
  "lovely",
  "sly",
  "tally",
  "wobbly",
  "woolly",
]);

/* ------------------------------------------------------------------ */
/*  Analysis                                                          */
/* ------------------------------------------------------------------ */

interface AdverbFinding {
  word: string;
  count: number;
}

interface AnalysisResult {
  wordCount: number;
  totalAdverbs: number;
  densityPer100: number;
  findings: AdverbFinding[];
  highlighted: ReactNode[];
}

function isAdverb(word: string): boolean {
  return !LY_EXCEPTIONS.has(word.toLowerCase());
}

function buildHighlighted(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let last = 0;
  for (const match of text.matchAll(RE_LY_WORD)) {
    if (!isAdverb(match[0])) {
      continue;
    }
    if (match.index > last) {
      nodes.push(text.slice(last, match.index));
    }
    nodes.push(
      <mark
        className="rounded bg-yellow-200 px-0.5 dark:bg-yellow-900/60 dark:text-yellow-100"
        key={`adverb-${match.index}`}
      >
        {match[0]}
      </mark>
    );
    last = match.index + match[0].length;
  }
  if (last < text.length) {
    nodes.push(text.slice(last));
  }
  return nodes;
}

function analyzeAdverbs(text: string): AnalysisResult | null {
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    return null;
  }

  const counts = new Map<string, number>();
  let totalAdverbs = 0;
  for (const match of text.matchAll(RE_LY_WORD)) {
    if (!isAdverb(match[0])) {
      continue;
    }
    const key = match[0].toLowerCase();
    counts.set(key, (counts.get(key) ?? 0) + 1);
    totalAdverbs += 1;
  }

  const wordCount = trimmed.split(RE_SPLIT_WORDS).filter(Boolean).length;
  const densityPer100 = wordCount > 0 ? (totalAdverbs / wordCount) * 100 : 0;

  const findings: AdverbFinding[] = Array.from(counts.entries())
    .map(([word, count]) => ({ word, count }))
    .sort((a, b) => b.count - a.count);

  return {
    wordCount,
    totalAdverbs,
    densityPer100,
    findings,
    highlighted: buildHighlighted(text),
  };
}

/* ------------------------------------------------------------------ */
/*  Stat card                                                         */
/* ------------------------------------------------------------------ */

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Page Component                                                    */
/* ------------------------------------------------------------------ */

/** English strings. A translated content file overrides these through `ui`. */
export const ADVERB_FINDER_STRINGS = {
  textToCheckForAdverbs: "Text to check for adverbs",
  pasteOrTypeYourText: "Paste or type your text here to find adverbs...",
  totalWords: "Total Words",
  adverbsFound: "Adverbs Found",
  uniqueAdverbs: "Unique Adverbs",
  per100Words: "Per 100 Words",
  yourTextWithAdverbsHighlighted: "Your Text with Adverbs Highlighted",
  adverbsByFrequency: "Adverbs by Frequency",
  occurrence: "one: occurrence|other: occurrences",
  noLyAdverbsDetected: "No -ly adverbs detected!",
  yourTextIsFreeOf:
    "Your text is free of -ly adverbs. Your verbs are doing the work.",
} as const;

export default function AdverbFinderWidget() {
  const [text, setText] = useState("");

  const analysis = useMemo(() => analyzeAdverbs(text), [text]);

  const locale = useToolLocale();
  const strings = useToolUi(ADVERB_FINDER_STRINGS);
  const fmt = useToolNumber();

  return (
    <>
      {/* Input */}
      <textarea
        aria-label={strings.textToCheckForAdverbs}
        className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        onChange={(e) => setText(e.target.value)}
        placeholder={strings.pasteOrTypeYourText}
        value={text}
      />
      {/* Stats */}
      {analysis && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard
            label={strings.totalWords}
            value={fmt(analysis.wordCount)}
          />
          <StatCard
            label={strings.adverbsFound}
            value={fmt(analysis.totalAdverbs)}
          />
          <StatCard
            label={strings.uniqueAdverbs}
            value={fmt(analysis.findings.length)}
          />
          <StatCard
            label={strings.per100Words}
            value={fmt(analysis.densityPer100, {
              minimumFractionDigits: 1,
              maximumFractionDigits: 1,
            })}
          />
        </div>
      )}
      {/* Highlighted text */}
      {analysis && analysis.totalAdverbs > 0 && (
        <section className="space-y-3">
          <h2 className="font-semibold text-foreground text-lg">
            {strings.yourTextWithAdverbsHighlighted}
          </h2>
          <div className="whitespace-pre-wrap rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed">
            {analysis.highlighted}
          </div>
        </section>
      )}
      {/* Frequency list */}
      {analysis && analysis.findings.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-semibold text-foreground text-lg">
            {strings.adverbsByFrequency}
          </h2>
          <div className="space-y-3">
            {analysis.findings.map((finding) => (
              <div
                className="flex items-center justify-between rounded-lg border border-border bg-card p-4"
                key={finding.word}
              >
                <span className="font-medium text-foreground text-sm">
                  {finding.word}
                </span>
                <span className="text-muted-foreground text-sm">
                  {fmt(finding.count)}
                  {tPlural(strings.occurrence, locale, finding.count)}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}
      {/* None found */}
      {analysis && analysis.totalAdverbs === 0 && (
        <div className="rounded-lg border border-border bg-card p-5 text-center">
          <p className="font-medium text-foreground">
            {strings.noLyAdverbsDetected}
          </p>
          <p className="mt-1 text-muted-foreground text-sm">
            {strings.yourTextIsFreeOf}
          </p>
        </div>
      )}
    </>
  );
}
